import { useEffect, useState } from 'react';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { Button } from '@/components/Button';
import { Combobox, IComboboxOption } from '@/components/Combobox';
import { Input } from '@/components/Input';
import { ModalComponent } from '@/components/ModalComponent';
import { ModalHeader } from '@/components/ModalHeader';
import {
    EBorrowerCitizenshipStatus,
    selectLicenseState,
    selectLicenseVersionNumber,
    selectPersonalDetails,
    selectResidencyStatus,
    selectVisaNumber,
    setLicenseVersionNumber,
} from '@/context/borrower';
import { EBorrowerLicenseType, setLicenseType } from '@/context/borrower';
import {
    EJourneys,
    selectInitialJourney,
    selectIsNZ,
    setModal,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { BirthDate } from '@/UserInput/BirthDate';
import { LicenseExpiry } from '@/UserInput/LicenseExpiry';
import { LicenseNumber } from '@/UserInput/LicenseNumber';
import { LicenseState } from '@/UserInput/LicenseState';
import { ResidencyStatus } from '@/UserInput/ResidencyStatus';
import { isBirthDateValid } from '@/utils/checkBirthDate';
import { isLicenseExpiryDateValid } from '@/utils/checkLicenseExpiryDate';
import { isVisaExpiryDateValid } from '@/utils/checkVisaExpiryDate';

export const testId = 'PersonalInformation';

export function PersonalInformation(): JSX.Element {
    // ***** Constants *****
    const baseId = 'modal-ContactDetails';
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState<boolean>(false);
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const licenseState = useAppSelector(selectLicenseState);
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const visaNumber = useAppSelector(selectVisaNumber);
    const licenseVersionNumber = useAppSelector(selectLicenseVersionNumber);
    const personal = useAppSelector(selectPersonalDetails);
    const initialJourney = useAppSelector(selectInitialJourney);
    const isNZ = useAppSelector(selectIsNZ);
    const isTestDrive = initialJourney === EJourneys.TEST_DRIVE;
    const [licenseTypeQuery, setLicenseTypeQuery] = useState<string>(
        (personal?.licenseType as string) ?? ''
    );

    useEffect(() => {
        setLicenseTypeQuery((personal?.licenseType as string) ?? '');
    }, [personal?.licenseType]);

    // ***** Handlers *****
    function handleClick(): void {
        setShowErrors(true);
        if (!isComplete()) return;
        createOrUpdateBorrower();
        dispatch(setModal(null));
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        if (!isBirthDateValid()) return false;
        if (licenseState === '' || licenseState === undefined) return false;
        if (!isLicenseExpiryDateValid()) return false;

        if (isTestDrive) {
            return true;
        }

        if (residencyStatus === null || residencyStatus === undefined)
            return false;
        if (
            residencyStatus === EBorrowerCitizenshipStatus.VISA_HOLDER &&
            !isNZ &&
            (visaNumber === undefined ||
                visaNumber === 0 ||
                !isVisaExpiryDateValid())
        )
            return false;
        return true;
    }

    // ***** Render *****
    return (
        <ModalComponent baseId={baseId} data-testid={testId}>
            <ModalHeader title='Personal Information' baseId={baseId} />
            <ResidencyStatus baseId={baseId} showErrors={showErrors} />
            {!isNZ && <LicenseNumber baseId={baseId} showErrors={showErrors} />}
            <LicenseState baseId={baseId} showErrors={showErrors} />
            <LicenseExpiry baseId={baseId} showErrors={showErrors} />
            {isNZ && (
                <div className='_flex space-x-2 flex-center'>
                    <div style={{ flex: 1 }}>
                        <Input
                            title='Licence Version Number'
                            placeholder='3 digits'
                            showErrors={showErrors && !licenseVersionNumber}
                            id={`${baseId}-input-licenseVersionNumber`}
                            value={licenseVersionNumber ?? ''}
                            onChange={(value): void => {
                                if (typeof value === 'boolean') return;
                                const digitsOnly = value.replace(/\D/g, '');
                                if (digitsOnly.length > 3) return;
                                dispatch(setLicenseVersionNumber(digitsOnly));
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Combobox
                            options={[
                                {
                                    id: 1,
                                    value: EBorrowerLicenseType.RESTRICTED,
                                },
                                { id: 2, value: EBorrowerLicenseType.FULL },
                                { id: 3, value: EBorrowerLicenseType.NONE },
                            ]}
                            placeholder='Restricted / Full / None'
                            heading='Licence Type'
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ): void => {
                                setLicenseTypeQuery(e.target.value);
                            }}
                            query={licenseTypeQuery}
                            onSelect={(option: IComboboxOption): void => {
                                const value = option.value as string;
                                setLicenseTypeQuery(value);
                                dispatch(setLicenseType(value));
                            }}
                            showErrors={false}
                            baseId={baseId}
                        />
                    </div>
                </div>
            )}
            <BirthDate baseId={baseId} showErrors={showErrors} />
            <div className='py-2 mx-16'>
                <Button onClick={handleClick} id={`${baseId}-button-close`}>
                    <p>Save</p>
                </Button>
            </div>
        </ModalComponent>
    );
}
